import React, { useState } from 'react';
import { styled } from '@mui/material';

import { IndividualAnalytics, WorkloadAnalytics } from 'components/molecules/Analytics';
import { MilestoneStatus, TaskStatus } from 'components/organisms/Dashboard';
import { useTask } from 'contexts/task';
import { useViewport } from 'contexts/viewport';
import { Accordion, AccordionSummary, AccordionDetails, Stack, Tabs, Text, Tooltip } from 'shell/components';
import { getAnalyticsStats } from 'utils/helper';

const Analytics = ({ students }) => {
    const { taskList, subtaskList } = useTask();
    const { device } = useViewport();

    const [defaultRoute, setDefaultRoute] = useState('workload');
    const isIndividual = students?.length === 1;

    const stats = [...(students || []), { owner_account_id: '' }].reduce((acc, cur) => {
        return {
            ...acc,
            [cur.owner_account_id || 'unassigned']: getAnalyticsStats(
                [...Object.values(taskList.all), ...Object.values(subtaskList)],
                cur.owner_account_id
            ),
        };
    }, {});

    const tabList = [
        {
            key: 'workload',
            value: 'Workload',
            content: <WorkloadAnalytics students={students} stats={stats} />,
        },
        {
            key: 'individual',
            value: device === 'tablet' ? 'Indiv.' : 'Individual',
            content: <IndividualAnalytics students={students} stats={stats} />,
        },
    ];

    const AnalyticsHeader = (
        <Stack alignItems={'center'} direction={'row'} spacing={2}>
            <Text variant={'sh2'} weight={'semibold'}>
                Analytics
            </Text>
            <Tooltip
                content={
                    <Stack spacing={4}>
                        <Text color={'inherit'} variant={'p3'}>
                            <Text color={'inherit'} component={'span'} variant={'p3'} weight={'semibold'}>
                                Tasks Points
                            </Text>{' '}
                            are used to calculate team or individual progress and workload distribution. They are broken
                            down based on the workload assigned:
                        </Text>
                        <Stack direction={'row'} justifyContent={'space-between'}>
                            <Text color={'inherit'} variant={'p3'}>
                                <Text color={'inherit'} component={'span'} variant={'p3'} weight={'semibold'}>
                                    High:
                                </Text>{' '}
                                4 pts
                            </Text>
                            <Text color={'inherit'} variant={'p3'}>
                                <Text color={'inherit'} component={'span'} variant={'p3'} weight={'semibold'}>
                                    Medium:
                                </Text>{' '}
                                2 pts
                            </Text>
                            <Text color={'inherit'} variant={'p3'}>
                                <Text color={'inherit'} component={'span'} variant={'p3'} weight={'semibold'}>
                                    Low:
                                </Text>{' '}
                                1 pt
                            </Text>
                        </Stack>
                    </Stack>
                }
                type={'info'}
            />
        </Stack>
    );

    const AnalyticsBody = (
        <CustomTabs
            aria-label={'select analytics tab'}
            options={tabList}
            selected={defaultRoute}
            onChange={setDefaultRoute}
        />
    );

    return (
        <CustomStack device={device} spacing={2}>
            {device === 'mobile' ? (
                <Accordion>
                    <AccordionSummary id={'analytics-header'} aria-controls={'analytics-content'}>
                        {AnalyticsHeader}
                    </AccordionSummary>
                    <AccordionDetails>
                        {isIndividual ? (
                            <Stack spacing={2}>
                                <MilestoneStatus
                                    milestones={Object.values(taskList.all)?.filter(
                                        (task) => task.type === 'milestone'
                                    )}
                                />
                                <TaskStatus
                                    tasks={Object.values(taskList.all)?.filter((task) => task.type !== 'milestone')}
                                />
                            </Stack>
                        ) : (
                            AnalyticsBody
                        )}
                    </AccordionDetails>
                </Accordion>
            ) : (
                <>
                    {AnalyticsHeader}
                    {isIndividual ? (
                        <Stack spacing={2}>
                            <MilestoneStatus
                                milestones={Object.values(taskList.all)?.filter((task) => task.type === 'milestone')}
                            />
                            <TaskStatus
                                tasks={Object.values(taskList.all)?.filter((task) => task.type !== 'milestone')}
                            />
                        </Stack>
                    ) : (
                        AnalyticsBody
                    )}
                </>
            )}
        </CustomStack>
    );
};

const CustomStack = styled(Stack)(({ device, theme }) => {
    return {
        minWidth: 0,
        padding: theme.spacing(5, 5, 0),
        background: theme.palette.ensightful.shades[0],
        ...(device === 'mobile' && {
            padding: theme.spacing(5),
        }),
    };
});

const CustomTabs = styled(Tabs)(({ theme }) => {
    return {
        '& button.MuiTab-root': {
            flex: 1,
            minWidth: 0,
        },
        '& > .MuiBox-root': {
            paddingBottom: theme.spacing(4),
        },
    };
});

export default Analytics;
